import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';

import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import infoIcon from '@nokia-csf-uxr/ccfk-assets/icons/info.svg';
import warningIcon from '@nokia-csf-uxr/ccfk-assets/icons/warning.svg';
import errorIcon from '@nokia-csf-uxr/ccfk-assets/icons/error.svg';

import Typography, { TypographyProps } from 'components/Typography';
import Icon, { IconProps } from 'components/Icon';

import Styled from './styles';

const TYPOGRAPHY_CONFIG = (RTL: boolean) => ({
  style: {
    marginLeft: RTL ? Tokens.SPACING.SPACING_0 : Tokens.SPACING.SPACING_16,
    marginRight: RTL ? Tokens.SPACING.SPACING_16 : Tokens.SPACING.SPACING_0,
    lineHeight: Tokens.LINE_HEIGHT.LINE_HEIGHT_NORMAL
  },
  variant: 'BODY',
});

const ICONS = {
  CONFIRM: infoIcon,
  WARNING: warningIcon,
  ERROR: errorIcon
};

export interface AlertMessageProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the message. */
  message: React.ReactNode;
  /** The Typography configuration. See `<Typography/>` documentation for more. */
  typographyProps?: Partial<TypographyProps>;
  /** The Icon configuration. See `<Icon/>` documentation for more. */
  iconProps?: Partial<IconProps>;
  /** The variant of the alert. */
  variant?: 'CONFIRM' | 'ERROR' | 'WARNING' | string | undefined;
  /** To override the content. */
  children?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}
const DEFAULT_PROPS = {
  typographyProps: {},
  iconProps: {},
  variant: 'CONFIRM',
  children: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  theme: {}
};

export interface ThemeShape {
  ALERTS: Record<string, unknown>
}

/** All other props will be provided to the wrapper element. */
const AlertMessage = React.forwardRef<HTMLElement, AlertMessageProps>((props: AlertMessageProps, ref) => getContext(({ RTL, theme }
: { theme: ThemeShape, RTL: boolean }) => {
  const {
    variant = DEFAULT_PROPS.variant,
    message,
    theme: themeProp,
    iconProps,
    typographyProps,
    children,
    ...otherProps
  } = props;

  const resolvedTheme: {} = _merge({}, theme ? theme.ALERTS : {}, themeProp);

  const resolvedTypographyProps: {} = _merge(
    {},
    TYPOGRAPHY_CONFIG(RTL),
    typographyProps
  );

  const resolvedIconProps = {
    src: ICONS[variant],
    color: resolvedTheme[variant],
    ...iconProps
  };

  const content = children === undefined
    ? (
      <>
        <Icon {...resolvedIconProps} />
        <Typography {...resolvedTypographyProps}>{message}</Typography>
      </>
    )
    : children;

  return (
    <Styled.AlertMessage
      ref={ref}
      $rtl={RTL}
      {...otherProps}
    >
      {content}
    </Styled.AlertMessage>
  );
}));

AlertMessage.displayName = 'AlertMessage';
AlertMessage.defaultProps = DEFAULT_PROPS;

export default AlertMessage;
